import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, UserCircle } from 'lucide-react';
import { navigationItems } from './navigation';
import { PAGE_TO_PATH } from '../../App';

import { Brand } from '../ui/Brand';

const ICON_SIZE = 18;

export function Sidebar({
  user, onNavigate, onLogout, onOpenProfile,
  onSwitchRestaurant, restaurant, theme, onToggleTheme,
  collapsed, onToggleCollapse, onMobileClose, onSwitchToEmployee,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = Object.entries(PAGE_TO_PATH).find(([, p]) => p === location.pathname)?.[0] || 'dashboard';

  const isPrivileged = user?.role === 'admin' || user?.role === 'general_manager';
  const isCommandCenter = isPrivileged && !restaurant;
  const navItems = navigationItems(user?.role, !!restaurant);
  const logoSub = isCommandCenter
    ? 'All Locations'
    : restaurant?.name || 'Select location';

  function handleNavClick(id) {
    navigate(PAGE_TO_PATH[id] || '/dashboard');
    if (onNavigate) onNavigate(id);
    if (onMobileClose) onMobileClose();
  }

  function handleBackToOverview() {
    if (onSwitchRestaurant) onSwitchRestaurant();
    if (onMobileClose) onMobileClose();
  }

  return (
    <aside className={`app-sidebar ${collapsed ? 'collapsed' : ''}`}>

      {/* COLLAPSE BUTTON (desktop only) */}
      <button
        className="sidebar-collapse-btn"
        onClick={onToggleCollapse}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed
          ? <ArrowLeft size={10} style={{ transform: 'rotate(180deg)' }} />
          : <ArrowLeft size={10} />}
      </button>

      <div className="sidebar-logo"><Brand compact={collapsed} inverse subtitle={logoSub} /></div>

      {/* NAV */}
      <nav className="sidebar-nav" role="navigation" aria-label="Main navigation">

        {/* Admin in restaurant mode: back to overview */}
        {isPrivileged && restaurant && (
          <button
            className="sidebar-item"
            onClick={handleBackToOverview}
            title={collapsed ? 'All Locations' : ''}
            aria-label="Back to all locations"
            style={{ color: 'var(--sidebar-text)', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '10px' }}
          >
            <span className="sidebar-item-icon"><ArrowLeft size={ICON_SIZE} /></span>
            <span>All Locations</span>
          </button>
        )}

        {navItems.flatMap(({ id, label, Icon, group }, index) => [
          ...(index === 0 || navItems[index - 1].group !== group ? [<div key={group} className="sidebar-section-label">{group}</div>] : []),
          <button
            key={id}
            className={`sidebar-item ${currentPage === id ? 'active' : ''}`}
            onClick={() => handleNavClick(id)}
            title={collapsed ? label : ''}
            aria-label={`Go to ${label}`}
            aria-current={currentPage === id ? 'page' : undefined}
          >
            <span className="sidebar-item-icon"><Icon size={ICON_SIZE} /></span>
            <span>{label}</span>
          </button>
        ])}

        <button
          className="sidebar-item"
          onClick={onToggleTheme}
          title={collapsed ? 'Toggle Theme' : ''}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <span className="sidebar-item-icon">
            {theme === 'dark' ? <Sun size={ICON_SIZE} /> : <Moon size={ICON_SIZE} />}
          </span>
          <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
      </nav>

      {/* USER */}
      <div className="sidebar-bottom">
        <button
          className="sidebar-item"
          onClick={onOpenProfile}
          title={collapsed ? `${user?.name} — Profile` : ''}
          aria-label="Open profile"
          style={{ paddingTop: '10px', paddingBottom: '10px' }}
        >
          <span className="sidebar-item-icon">
            <div style={{ width: `${ICON_SIZE + 4}px`, height: `${ICON_SIZE + 4}px`, borderRadius: '50%', background: 'var(--brand)', color: 'var(--brand-contrast)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '900', flexShrink: 0 }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--sidebar-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize: '10px', color: 'var(--sidebar-text-muted)', textTransform: 'capitalize', marginTop: '1px' }}>{user?.role?.replaceAll('_', ' ')}</div>
          </div>
        </button>

        {onSwitchToEmployee && (
          <button
            className="sidebar-item"
            onClick={onSwitchToEmployee}
            title={collapsed ? 'Employee View' : ''}
            aria-label="Switch to employee view"
          >
            <span className="sidebar-item-icon"><UserCircle size={ICON_SIZE} /></span>
            <span>Employee View</span>
          </button>
        )}
      </div>

    </aside>
  );
}
